import { fetchWithAuth } from "../utils/fetchWithAuth";

// Reconstruct pill phases for a saved bot message.
// Priority:
//   1. Saved phases already include C or W → use as-is (correctly saved new messages)
//   2. No C/W in saved phases but agentExchangeLog exists → rebuild C pill from the log
//   3. Fallback → single T done pill
function reconstructPhasesForMessage(msg) {
  if (msg.sender !== 'bot') return undefined;

  const saved = msg.phases;
  const log = msg.agentExchangeLog || [];

  // If we already have C or W pills saved, trust them directly
  // Exception: if saved C tasks are missing agentInfo.detail, try to rebuild from agentExchangeLog
  if (saved && saved.some(p => p.id === 'C' || p.id === 'W')) {
    const savedC = saved.find(p => p.id === 'C');
    const cTasksMissingDetail = savedC && savedC.tasks?.length > 0
      && savedC.tasks.every(t => !t.agentInfo?.detail?.length);
    if (!cTasksMissingDetail) return saved;
    // Fall through to rebuild from agentExchangeLog if available
  }

  // Rebuild C pill from agentExchangeLog if available
  if (log.length > 0) {
    const tasks = log.map((entry, idx) => {
      if (entry.task) {
        const { task, result } = entry;
        return {
          id: task.task_id || `task-${idx}`,
          label: result?.agent_name || task.agent || 'Agent',
          state: 'done',
          agentInfo: {
            id: task.task_id || `task-${idx}`,
            name: result?.agent_name || task.agent || 'Agent',
            state: 'done',
            isLead: false,
            instruction: task.instruction || '',
            detail: result ? [{ type: 'result', ...result }] : [],
          },
        };
      }
      return {
        id: entry.agentId || `agent-${idx}`,
        label: entry.name || entry.agent_name || 'Agent',
        state: 'done',
        agentInfo: entry,
      };
    });
    // Preserve any non-C phases from saved data (e.g. W phases), merge rebuilt C in
    const nonCPhases = (saved || []).filter(p => p.id !== 'C');
    const tPhase = nonCPhases.find(p => p.id === 'T') || { id: 'T', state: 'done', tasks: [] };
    const otherPhases = nonCPhases.filter(p => p.id !== 'T');
    return [tPhase, { id: 'C', state: 'done', tasks }, ...otherPhases];
  }

  // Default: T only
  return saved || [{ id: 'T', state: 'done', tasks: [] }];
}

function hasPersistableMessageContent(msg) {
  if (!msg) return false;
  if (msg.isProgressUI || msg.isProgressUIUpdate || msg.isLoading || msg.isPlaceholder) return false;
  if (msg.text?.trim() && msg.text !== "Workflow Progress") return true;
  if (msg.closingText?.trim()) return true;
  if (msg.agentToolCalls?.length > 0) return true;
  if (msg.segments?.some(seg =>
    seg.type === "tool" ||
    seg.displayContent?.trim() ||
    seg.rawContent?.trim()
  )) return true;
  return msg.phases?.some(phase => phase.tasks?.length > 0);
}

class ChatStateManager {
  constructor() {
    this.conversations = new Map();
    this.activeConversationId = null;
    this.listeners = new Set();
    this.isInitialized = false;
    this._isAddingMessage = false; // Recursion guard
    this._initPromise = null;      // In-flight loadConversationsForPlan promise
    this._initPlanId = null;       // Plan ID currently being loaded
    this._pendingConversation = null; // Conversation waiting in debounce queue
  }

  // Singleton pattern
  static getInstance() {
    if (!ChatStateManager.instance) {
      ChatStateManager.instance = new ChatStateManager();
      // Register once — flush any unsaved conversation before page unloads
      if (typeof window !== 'undefined') {
        window.addEventListener('beforeunload', () => {
          ChatStateManager.instance.flushPendingSave();
        });
      }
    }
    return ChatStateManager.instance;
  }

  // CRITICAL FIX: Reset chat state when switching plans to prevent cross-plan conversation loading
  resetForPlanSwitch() {
    console.log("🔄 ChatStateManager: Resetting state for plan switch");
    this.activeConversationId = null;
    this.currentPlanId = null;
    this.isInitialized = false;
    this._initPromise = null;
    this._initPlanId = null;
    // Keep conversations in memory for caching, but clear active state
    console.log("✅ ChatStateManager: State reset complete");
  }

  // CRITICAL FIX: Full reset for new plan creation - clears all conversation state
  // Use this when starting a brand new Decision Call or Full Strategy to prevent stale messages
  resetForNewPlanCreation() {
    console.log("🔄 ChatStateManager: Full reset for NEW plan creation");
    this.activeConversationId = null;
    this.currentPlanId = null;
    this.isInitialized = false;
    
    // Clear all in-memory conversations to prevent stale progress UI from previous tests
    this.conversations.clear();
    
    console.log("✅ ChatStateManager: Full reset complete - conversations cleared");
    
    // Notify listeners of the reset
    this.notifyListeners({
      type: "full_reset",
      reason: "new_plan_creation"
    });
  }

  // Initialize chat state for a plan
  async initializeForPlan(planId) {
    console.log("🔥🔥🔥 CHAT LOG: ChatStateManager.initializeForPlan() called", {
      planId,
      currentPlanId: this.currentPlanId,
      isInitialized: this.isInitialized,
      conversationsCount: this.conversations.size,
      conversationIds: Array.from(this.conversations.keys())
    });

    // Plan ids arrive as numbers from saves and as strings from the URL.
    const samePlan = (left, right) => String(left ?? "") === String(right ?? "");

    if (this.isInitialized && samePlan(this.currentPlanId, planId)) {
      console.log("🔥🔥🔥 CHAT LOG: Already initialized for plan", planId);
      // If a load is still in flight, wait for it before returning
      if (this._initPromise) await this._initPromise;
      return await this.getActiveConversation();
    }

    // If another initializeForPlan call is already in flight for the same plan, wait for it
    if (this._initPromise && samePlan(this._initPlanId, planId)) {
      console.log("🔥🔥🔥 CHAT LOG: initializeForPlan already in flight for plan", planId, "— waiting");
      await this._initPromise;
      return await this.getActiveConversation();
    }

    console.log("🔥🔥🔥 CHAT LOG: Initializing for plan", planId);

    // Only reset conversation when switching between DIFFERENT existing plans
    // Don't reset when transitioning from null (strategy creation) to new plan
    if (this.currentPlanId && !samePlan(this.currentPlanId, planId) && planId !== null) {
      console.log(
        "ChatStateManager: Switching from plan",
        this.currentPlanId,
        "to",
        planId,
      );
      this.activeConversationId = null;
    }

    this.currentPlanId = planId;

    // Load existing conversations for this plan and WAIT for completion
    // Track the in-flight promise so concurrent callers wait instead of racing
    console.log("🔥🔥🔥 CHAT LOG: About to loadConversationsForPlan for plan", planId);
    this._initPlanId = planId;
    this._initPromise = this.loadConversationsForPlan(planId);
    await this._initPromise;
    this._initPromise = null;
    this._initPlanId = null;
    this.isInitialized = true; // Set AFTER conversations are loaded
    console.log("🔥🔥🔥 CHAT LOG: Completed loadConversationsForPlan, conversations in memory:", {
      totalConversations: this.conversations.size,
      conversationsForPlan: Array.from(this.conversations.values()).filter(c => c.planId == planId).length
    });

    // Client-created plans may now have a saved first-entry welcome. Other
    // plans keep their existing creation conversation or an empty placeholder.
    console.log("🔥🔥🔥 CHAT LOG: About to getActiveConversation");
    const conversation = await this.getActiveConversation();
    console.log("🔥🔥🔥 CHAT LOG: Got active conversation:", {
      id: conversation?.id,
      planId: conversation?.planId,
      messagesCount: conversation?.messages?.length || 0,
      firstMessage: conversation?.messages?.[0]?.text?.substring(0, 100) + '...' || 'No messages'
    });

    // Notify the chat even when there was no persisted conversation to load.
    const planConversations = Array.from(this.conversations.values()).filter(
      (c) => c.planId == planId && c.isPersisted,
    );
    if (planConversations.length === 0) {
      setTimeout(() => {
        this.notifyListeners({
          type: 'conversation_loaded',
          conversationId: conversation?.id,
          conversation,
        });
      }, 0);
    }

    return conversation;
  }

  // Find existing conversation for a specific plan
  findConversationForPlan(planId) {
    const planConversations = [];
    for (const [, conversation] of this.conversations.entries()) {
      // CRITICAL FIX: Use loose equality (==) to handle string vs number planId mismatch
      // Database might return number, but URL param might be string
      if (conversation.planId == planId) {
        planConversations.push(conversation);
      }
    }
    
    console.log(`🔍 ChatStateManager: Found ${planConversations.length} conversations in memory for plan ${planId}:`, 
      planConversations.map(c => ({
        id: c.id,
        messagesCount: c.messages?.length || 0,
        firstMessage: c.messages?.[0]?.text?.substring(0, 50) + '...' || 'No messages'
      }))
    );
    
    // Return the most recent one. The API returns newest first, but do not rely
    // on Map insertion order because conversations can also be added locally.
    return planConversations
      .sort(
        (a, b) =>
          new Date(b.lastActive || b.createdAt || 0) -
          new Date(a.lastActive || a.createdAt || 0),
      )[0] || null;
  }

  // FIX #43: Get or create conversation for brief page (no planId)
  async getOrCreateBriefConversation() {
    console.log("🔥 ChatStateManager: getOrCreateBriefConversation called");
    
    // Look for existing brief conversation (planId is null)
    const briefConversations = [];
    for (const [, conversation] of this.conversations.entries()) {
      if (conversation.planId === null || conversation.planId === undefined) {
        briefConversations.push(conversation);
      }
    }
    
    if (briefConversations.length > 0) {
      // Return the most recent brief conversation
      const existingConversation = briefConversations[briefConversations.length - 1];
      console.log(`✅ ChatStateManager: Found existing brief conversation ${existingConversation.id} with ${existingConversation.messages?.length || 0} messages`);
      this.activeConversationId = existingConversation.id;
      this.currentPlanId = null; // Explicitly set to null for brief context
      return existingConversation;
    }
    
    // Create new brief conversation
    console.log("📝 ChatStateManager: Creating new brief conversation");
    this.currentPlanId = null;
    const newConversation = this.createNewConversation(null);
    console.log(`✅ ChatStateManager: Created brief conversation ${newConversation.id}`);
    return newConversation;
  }

  // Create a new conversation
  createNewConversation(planId, initialMessage = null) {
    const conversationId = `conv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    const messages = [];
    if (initialMessage) {
      messages.push(initialMessage);
    }

    const conversation = {
      id: conversationId,
      planId: planId,
      messages: messages,
      createdAt: new Date().toISOString(),
      lastActive: new Date().toISOString(),
      isPersisted: false,
      hasUserInteraction: false, // Track if user has actually interacted
    };

    this.conversations.set(conversationId, conversation);
    this.activeConversationId = conversationId;

    console.log("ChatStateManager: Created new conversation", conversationId);
    this.notifyListeners({
      type: "conversation_created",
      conversationId,
      conversation,
    });

    // DON'T auto-save until user actually sends a message

    return conversation;
  }

  // Get active conversation or create one
  async getActiveConversation() {
    if (
      !this.activeConversationId ||
      !this.conversations.has(this.activeConversationId)
    ) {
      // Before creating a new conversation, look for an existing persisted one for this plan
      if (this.currentPlanId) {
        const existing = this.findConversationForPlan(this.currentPlanId);
        if (existing) {
          console.log(`✅ ChatStateManager: Found existing conversation ${existing.id} for plan ${this.currentPlanId} in early-return branch`);
          this.activeConversationId = existing.id;
          if (existing.isPersisted && (!existing.messages || existing.messages.length === 0)) {
            console.log("🔥🔥🔥 CHAT LOG: Loading messages for persisted conversation (early-return branch)", existing.id);
            await this.loadConversation(existing.id);
          }
          return existing;
        }
      }
      return this.createNewConversation(this.currentPlanId);
    }
    
    const activeConversation = this.conversations.get(this.activeConversationId);
    
    // CRITICAL FIX: Ensure active conversation belongs to current plan context
    // If current context is plan-specific, conversation must match that plan
    console.log(`🔍 ChatStateManager: Checking conversation context match:`, {
      conversationId: activeConversation.id,
      conversationPlanId: activeConversation.planId,
      conversationPlanIdType: typeof activeConversation.planId,
      currentPlanId: this.currentPlanId,
      currentPlanIdType: typeof this.currentPlanId,
      areEqual: activeConversation.planId === this.currentPlanId,
      areEqualLoose: activeConversation.planId == this.currentPlanId
    });
    
    if (this.currentPlanId && activeConversation.planId != this.currentPlanId) {
      console.log(`🔄 ChatStateManager: Active conversation ${activeConversation.id} belongs to plan ${activeConversation.planId}, but current context is plan ${this.currentPlanId}. Finding plan-specific conversation.`);
      
      // Look for existing conversation for this plan
      const planConversation = this.findConversationForPlan(this.currentPlanId);
      if (planConversation) {
        console.log(`✅ ChatStateManager: Found existing conversation ${planConversation.id} for plan ${this.currentPlanId}`);
        this.activeConversationId = planConversation.id;
        
        // If conversation is persisted but has no messages loaded, load them
        if (planConversation.isPersisted && (!planConversation.messages || planConversation.messages.length === 0)) {
          console.log("🔥🔥🔥 CHAT LOG: Loading messages for persisted conversation", planConversation.id);
          await this.loadConversation(planConversation.id);
          console.log("🔥🔥🔥 CHAT LOG: After loading conversation", planConversation.id, "- messages count:", planConversation.messages?.length || 0);
        }
        
        return planConversation;
      }
      
      // Create new conversation for this plan
      console.log(`🆕 ChatStateManager: Creating new conversation for plan ${this.currentPlanId}`);
      return this.createNewConversation(this.currentPlanId);
    }
    
    // If the active conversation is a new empty placeholder and a persisted
    // conversation has since been loaded for this plan, prefer the persisted one.
    if (!activeConversation.isPersisted && (!activeConversation.messages || activeConversation.messages.length === 0)) {
      const persistedConv = Array.from(this.conversations.values())
        .filter(c => c.planId == this.currentPlanId && c.isPersisted)
        .sort((a, b) => new Date(b.lastActive || b.createdAt) - new Date(a.lastActive || a.createdAt))[0];
      if (persistedConv) {
        console.log(`✅ ChatStateManager: Switching from empty placeholder to persisted conversation ${persistedConv.id}`);
        this.activeConversationId = persistedConv.id;
        if (!persistedConv.messages || persistedConv.messages.length === 0) {
          await this.loadConversation(persistedConv.id);
        }
        return persistedConv;
      }
    }

    return activeConversation;
  }

  // Replace a message by id in the active conversation
  replaceMessage(id, newMessage) {
    const conversation = this.activeConversationId
      ? this.conversations.get(this.activeConversationId)
      : null;
    if (!conversation) return false;

    const idx = conversation.messages.findIndex((m) => m.id === id);
    if (idx === -1) return false;

    conversation.messages[idx] = {
      ...newMessage,
      id,
      timestamp: newMessage.timestamp || new Date().toISOString(),
    };
    conversation.lastActive = new Date().toISOString();

    setTimeout(() => {
      this.notifyListeners({
        type: "message_updated",
        conversationId: conversation.id,
      });
    }, 0);

    return true;
  }

  // Add message to active conversation
  async addMessage(message) {
    const conversation = await this.getActiveConversation();
    if (!conversation) {
      console.error(
        "ChatStateManager: No active conversation to add message to",
      );
      return;
    }

    // Prevent recursive calls by checking if we're already processing
    if (this._isAddingMessage) {
      console.log("ChatStateManager: Prevented recursive addMessage call");
      return;
    }

    this._isAddingMessage = true;

    try {
      // Prevent duplicate messages
      const isDuplicate = conversation.messages.some(
        (msg) =>
          msg.text === message.text &&
          msg.sender === message.sender &&
          Math.abs(
            new Date(msg.timestamp || 0) -
              new Date(message.timestamp || Date.now()),
          ) < 1000,
      );

      if (isDuplicate) {
        console.log("ChatStateManager: Prevented duplicate message");
        return;
      }

      // Normalize message text field from various possible sources
      const text = message.text || message.message || message.content || "";
      
      const messageWithTimestamp = {
        ...message,
        text: text,
        timestamp: message.timestamp || new Date().toISOString(),
        isPlaceholder: message.isPlaceholder || false,
      };

      conversation.messages.push(messageWithTimestamp);
      conversation.lastActive = new Date().toISOString();

      // Mark conversation as having user interaction when user sends first message
      if (message.sender === "user" && !conversation.hasUserInteraction) {
        conversation.hasUserInteraction = true;
        console.log(
          "ChatStateManager: First user interaction detected, conversation will be saved",
        );
      }

      console.log(
        "ChatStateManager: Added message to conversation",
        conversation.id,
      );

      // Notify listeners after a microtask to prevent immediate recursion
      setTimeout(() => {
        this.notifyListeners({
          type: "message_added",
          conversationId: conversation.id,
          message: messageWithTimestamp,
        });
      }, 0);

      // Only auto-save conversation if user has interacted
      if (conversation.hasUserInteraction) {
        this.debouncedSave(conversation);
      }
    } finally {
      this._isAddingMessage = false;
    }
  }

  // Plan creation: store the finished creation chat with the new plan and wait
  // for the save. The progress, workflow and closing messages only live in the
  // chat's own state until now, so the plan page reloading the conversation
  // would otherwise find the database copy without them.
  async persistCreationConversation(messages, planId) {
    const conversation = this.activeConversationId
      ? this.conversations.get(this.activeConversationId)
      : null;
    if (!conversation) return;
    conversation.messages = (messages || []).filter(hasPersistableMessageContent);
    if (conversation.messages.some(message => message.sender === "user")) conversation.hasUserInteraction = true;
    if (planId) {
      conversation.planId = planId;
      this.currentPlanId = planId;
    }
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
      this.saveTimeout = null;
      this._pendingConversation = null;
    }
    await this.saveConversation(conversation);
  }

  // Sync inPlace React state to ChatStateManager after execution_done — ensures manual refresh persists
  syncInPlace(messages) {
    if (!this.activeConversationId) return;
    const conv = this.conversations.get(this.activeConversationId);
    if (!conv?.hasUserInteraction) return;
    // Replace conversation messages with the finalized React state (exclude loading/progress/placeholder)
    conv.messages = messages.filter(hasPersistableMessageContent);
    this.debouncedSave(conv);
  }

  // SYNCHRONOUS upsert for progress messages - prevents race conditions
  // This method operates directly on the cached conversation without async operations
  upsertProgressMessage(progressId, message) {
    if (!this.activeConversationId) {
      console.warn("ChatStateManager.upsertProgressMessage: No active conversation");
      return false;
    }

    const conversation = this.conversations.get(this.activeConversationId);
    if (!conversation) {
      console.warn("ChatStateManager.upsertProgressMessage: Active conversation not found in cache");
      return false;
    }

    // Find existing progress message with matching progressId
    const existingIndex = conversation.messages.findIndex(
      (msg) => (msg.isProgressUI || msg.isProgressUIUpdate) && 
               (msg.progressId || 'default') === progressId
    );

    const progressMessage = {
      ...message,
      progressId: progressId,
      isProgressUI: true,
      isProgressUIUpdate: existingIndex !== -1,
      sender: "bot",
      timestamp: message.timestamp || new Date().toISOString(),
    };

    if (existingIndex !== -1) {
      // UPDATE existing message in place
      console.log("🔄 ChatStateManager.upsertProgressMessage: UPDATING existing at index", existingIndex, "progressId:", progressId);
      conversation.messages[existingIndex] = {
        ...conversation.messages[existingIndex],
        ...progressMessage,
        workflowSteps: message.workflowSteps || conversation.messages[existingIndex].workflowSteps,
        currentStep: message.currentStep !== undefined ? message.currentStep : conversation.messages[existingIndex].currentStep,
      };
    } else {
      // ADD new progress message
      console.log("➕ ChatStateManager.upsertProgressMessage: ADDING new progress message, progressId:", progressId);
      conversation.messages.push(progressMessage);
    }

    conversation.lastActive = new Date().toISOString();

    // Notify listeners synchronously
    this.notifyListeners({
      type: existingIndex !== -1 ? "message_updated" : "message_added",
      conversationId: conversation.id,
      message: progressMessage,
    });

    return true;
  }

  // Replace the last placeholder message with actual content
  async replaceLastPlaceholder(content, messageData = {}) {
    if (!this.activeConversationId) {
      console.warn("No active conversation to update placeholder");
      return null;
    }

    const activeConversation = this.conversations.get(this.activeConversationId);
    if (!activeConversation) {
      console.warn("Active conversation not found for placeholder replacement");
      return null;
    }

    // Find the last bot message with isPlaceholder = true
    for (let i = activeConversation.messages.length - 1; i >= 0; i--) {
      const message = activeConversation.messages[i];
      if (message.sender === "bot" && message.isPlaceholder) {
        // Update the placeholder with actual content
        message.text = content;
        message.isPlaceholder = false;
        message.isLoading = false; // Clear loading state
        message.timestamp = new Date().toISOString();
        
        // Apply any additional message data
        Object.assign(message, messageData);
        
        activeConversation.lastActive = new Date().toISOString();

        console.log("ChatStateManager: Replaced placeholder with actual content", {
          conversationId: this.activeConversationId,
          messageIndex: i,
          contentLength: content.length
        });

        this.notifyListeners({
          type: "message_updated",
          conversationId: this.activeConversationId,
          message,
          messageIndex: i
        });

        // Save conversation after replacing placeholder
        if (activeConversation.hasUserInteraction) {
          this.debouncedSave(activeConversation);
        }

        return message;
      }
    }

    console.warn("No placeholder bot message found to replace");
    return null;
  }

  // Load conversation by ID
  async loadConversation(conversationId) {
    console.log("🔥🔥🔥 CHAT LOG: loadConversation() called for", conversationId, "- current plan context:", this.currentPlanId);
    console.log("🔥🔥🔥 CHAT LOG: Loading conversation", conversationId);
    
    // First check if we have it in memory
    if (this.conversations.has(conversationId)) {
      this.activeConversationId = conversationId;
      const conversation = this.conversations.get(conversationId);

      // If the conversation has no messages or needs refresh, load them from backend
      if ((conversation.messages.length === 0 && conversation.isPersisted) || !conversation.messagesLoaded) {
        try {
          const response = await fetchWithAuth(
            `/api/get-conversation/${conversationId}`,
          );

          if (response.ok) {
            const fullConversation = await response.json();
            console.log("🔥 DEBUG: Raw conversation data from backend:", {
              conversationId,
              hasConversation: !!fullConversation.conversation,
              rawConversation: fullConversation.conversation ? fullConversation.conversation.substring(0, 200) + '...' : 'No conversation data'
            });
            
            const parsedMessages = fullConversation.conversation
              ? JSON.parse(fullConversation.conversation)
              : [];
            
            console.log("🔥 DEBUG: Parsed messages from backend:", {
              conversationId,
              totalParsedMessages: parsedMessages.length,
              messages: parsedMessages.map((msg, index) => ({
                index,
                text: (msg.message || msg.text || msg.content)?.substring(0, 50) + '...',
                sender: msg.sender,
                timestamp: msg.timestamp
              }))
            });
            
            const loadedMessages = parsedMessages
                  .filter((msg) => hasPersistableMessageContent({
                    ...msg,
                    text: msg.message || msg.text || msg.content || "",
                  }))
                  .map((msg) => ({
                    text: msg.message || msg.text || msg.content,
                    sender: msg.sender,
                    timestamp: msg.timestamp,
                    isHtml: false,
                    source: msg.source || undefined,
                    email_subject: msg.email_subject || undefined,
                    agentExchangeLog: msg.agentExchangeLog || undefined,
                    agent_info: msg.agent_info || undefined,
                    agentRunId: msg.agentRunId || msg.agent_run_id || undefined,
                    agentRunStatus: msg.agentRunStatus || undefined,
                    agentRunPhase: msg.agentRunPhase || undefined,
                    phases: reconstructPhasesForMessage(msg),
                    closingText: msg.closingText || undefined,
            agentToolCalls: msg.agentToolCalls?.length > 0 ? msg.agentToolCalls.map(tc => ({
              tool_call_id: tc.tool_call_id,
              tool:         tc.tool,
              description:  tc.description || undefined,
              params:       tc.params || undefined,
              result:       tc.result || undefined,
              status:       tc.status,
            })) : undefined,
            segments: msg.segments?.length > 0 ? msg.segments : undefined,
                  }));

            // Deduplicate: remove messages with the same sender+text (see create_plan bug)
            const seenKeys = new Set();
            let dedupedMessages = loadedMessages.filter(msg => {
              const keyText = (msg.text || '').trim();
              const key = keyText
                ? `${msg.sender}::${keyText}`
                : `${msg.sender}::${msg.timestamp || ''}::${msg.phases?.map(p => p.id).join(',') || ''}::${msg.agentToolCalls?.map(tc => tc.tool_call_id || tc.tool).join(',') || ''}`;
              if (seenKeys.has(key)) return false;
              seenKeys.add(key);
              return true;
            });

            // Sort chronologically. Messages without timestamps sort first (early messages
            // saved before timestamp tracking was added — treat as pre-existing context).
            dedupedMessages = dedupedMessages
              .map((msg, i) => ({ msg, i }))
              .sort((a, b) => {
                const ta = a.msg.timestamp ? new Date(a.msg.timestamp).getTime() : -1;
                const tb = b.msg.timestamp ? new Date(b.msg.timestamp).getTime() : -1;
                if (ta !== tb) return ta - tb;
                return a.i - b.i;
              })
              .map(({ msg }) => msg);

            // Update conversation with loaded messages
            conversation.messages = dedupedMessages;
            conversation.messagesLoaded = true;

            console.log(
              "ChatStateManager: Loaded messages for conversation",
              conversationId,
              "count:",
              loadedMessages.length,
            );
          }
        } catch (error) {
          console.error(
            "ChatStateManager: Failed to load conversation messages",
            error,
          );
        }
      }

      console.log("ChatStateManager: Loaded conversation", conversationId, "with", conversation.messages.length, "messages");
      
      // Always notify listeners when loading a conversation
      setTimeout(() => {
        this.notifyListeners({
          type: "conversation_loaded",
          conversationId,
          conversation,
        });
      }, 0);

      return conversation;
    }

    // If not in memory, try to load from backend
    try {
      const response = await fetchWithAuth(
        `/api/get-conversation/${conversationId}`,
      );

      if (response.ok) {
        const backendConversation = await response.json();

        console.log("🔥 DEBUG: Raw conversation from backend (not in memory):", {
          conversationId,
          hasConversation: !!backendConversation.conversation,
          rawConversation: backendConversation.conversation ? backendConversation.conversation.substring(0, 200) + '...' : 'No conversation data'
        });
        
        const parsedMessages = backendConversation.conversation
          ? JSON.parse(backendConversation.conversation)
          : [];
        
        console.log("🔥 DEBUG: Parsed messages from backend (not in memory):", {
          conversationId,
          totalParsedMessages: parsedMessages.length,
          messages: parsedMessages.map((msg, index) => ({
            index,
            text: (msg.message || msg.text || msg.content)?.substring(0, 50) + '...',
            sender: msg.sender,
            timestamp: msg.timestamp
          }))
        });

        // Create conversation object from backend data
        let backendMessages = parsedMessages
          .filter((msg) => {
            const messageText = msg.message || msg.text || msg.content;
            const isValid = messageText &&
              messageText.trim() !== "" &&
              messageText !== "Workflow Progress";
            console.log(`🔥 DEBUG: Message ${parsedMessages.indexOf(msg)} filter result (not in memory):`, {
              text: messageText?.substring(0, 50) + '...',
              sender: msg.sender,
              isValid
            });
            return isValid;
          })
          .map((msg) => ({
            text: msg.message || msg.text || msg.content,
            sender: msg.sender,
            timestamp: msg.timestamp,
            isHtml: false,
            agentExchangeLog: msg.agentExchangeLog || undefined,
            agent_info: msg.agent_info || undefined,
            agentRunId: msg.agentRunId || msg.agent_run_id || undefined,
            agentRunStatus: msg.agentRunStatus || undefined,
            agentRunPhase: msg.agentRunPhase || undefined,
            phases: reconstructPhasesForMessage(msg),
            closingText: msg.closingText || undefined,
            agentToolCalls: msg.agentToolCalls?.length > 0 ? msg.agentToolCalls : undefined,
            segments: msg.segments?.length > 0 ? msg.segments : undefined,
          }));

        // Deduplicate + sort (same logic as loadConversationsForPlan)
        const seenBackendKeys = new Set();
        backendMessages = backendMessages.filter(msg => {
          const key = `${msg.sender}::${(msg.text || '').trim()}`;
          if (seenBackendKeys.has(key)) return false;
          seenBackendKeys.add(key);
          return true;
        });
        backendMessages = backendMessages
          .map((msg, i) => ({ msg, i }))
          .sort((a, b) => {
            const ta = a.msg.timestamp ? new Date(a.msg.timestamp).getTime() : -1;
            const tb = b.msg.timestamp ? new Date(b.msg.timestamp).getTime() : -1;
            if (ta !== tb) return ta - tb;
            return a.i - b.i;
          })
          .map(({ msg }) => msg);

        const conversation = {
          id: backendConversation.id,
          planId: backendConversation.plan_id,
          title: backendConversation.title,
          createdAt: backendConversation.created_at,
          lastActive: backendConversation.timestamp,
          isPersisted: true,
          hasUserInteraction: true,
          messagesLoaded: true,
          messages: backendMessages,
        };

        // Store in memory
        this.conversations.set(conversationId, conversation);
        this.activeConversationId = conversationId;

        console.log(
          "ChatStateManager: Loaded conversation from backend",
          conversationId,
          "with",
          conversation.messages.length,
          "messages"
        );
        
        // Notify listeners
        setTimeout(() => {
          this.notifyListeners({
            type: "conversation_loaded",
            conversationId,
            conversation,
          });
        }, 0);

        return conversation;
      }
    } catch (error) {
      console.error(
        "ChatStateManager: Failed to load conversation from backend",
        error,
      );
    }

    console.error("ChatStateManager: Conversation not found", conversationId);
    return null;
  }

  // Start new chat (explicit user action)
  async startNewChat(forceReset = false) {
    if (!forceReset) {
      console.log("ChatStateManager: Ignoring automatic new chat request");
      return await this.getActiveConversation();
    }

    console.log("ChatStateManager: Starting new chat (user requested)");

    // Create welcome message for new chat
    // CRITICAL FIX: Use different welcome message depending on context
    // Brief page (no plan) = pure consultant mode
    // Plan page (has plan) = plan assistance mode
    const welcomeText = this.currentPlanId 
      ? "Hi! I'm here to help you with your design plan. Feel free to ask me anything about your strategy, programs, or tools. You can change things in your plan or ask me to do it"
      : "Hi! I'm your design strategy consultant. Share your project idea or challenge, and I'll help you think through it with expert guidance on design thinking, UX research, and product strategy.";
    
    const welcomeMessage = {
      text: welcomeText,
      sender: "bot",
      timestamp: new Date().toISOString(),
      isHtml: false,
    };

    const newConversation = this.createNewConversation(
      this.currentPlanId,
      welcomeMessage,
    );

    return newConversation;
  }

  // Initialize conversation for new plan with design challenge context
  initializeForNewPlan(planId, designChallenge = null) {
    console.log(
      "ChatStateManager: Initializing for new plan",
      planId,
      "with design challenge",
    );
    this.currentPlanId = planId;
    this.isInitialized = true;

    const conversation = this.createNewConversation(planId);

    // Store design challenge for title generation if provided
    if (designChallenge) {
      conversation.designChallenge = designChallenge;
    }

    return conversation;
  }

  // Subscribe to state changes
  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  // Notify all listeners
  notifyListeners(event) {
    this.listeners.forEach((listener) => {
      try {
        listener(event);
      } catch (error) {
        console.error("ChatStateManager: Listener error", error);
      }
    });
  }

  // Debounced save to prevent excessive API calls
  debouncedSave(conversation) {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
    }

    this._pendingConversation = conversation;
    this.saveTimeout = setTimeout(() => {
      this._pendingConversation = null;
      this.saveTimeout = null;
      this.saveConversation(conversation);
    }, 2000);
  }

  // Flush pending save immediately using keepalive fetch — safe to call in beforeunload
  flushPendingSave() {
    if (!this.saveTimeout || !this._pendingConversation) return;
    clearTimeout(this.saveTimeout);
    this.saveTimeout = null;

    const conversation = this._pendingConversation;
    this._pendingConversation = null;

    if (!conversation?.hasUserInteraction) return;

    const saveableMessages = (conversation.messages || []).filter(hasPersistableMessageContent);
    if (!saveableMessages.length) return;

    const planIdToUse = conversation.planId || this.currentPlanId || null;
    const firstUserMsg = saveableMessages.find(m => m.sender === 'user');
    const titleSource = firstUserMsg?.text || saveableMessages[0]?.text || 'New Conversation';
    const title = titleSource.length > 60 ? titleSource.substring(0, 60) + '...' : titleSource;

    const payload = {
      conversation_id: String(conversation.id).startsWith('conv_') ? null : conversation.id,
      plan_id: planIdToUse,
      title,
      messages: saveableMessages.map(msg => ({
        id: msg.id || undefined,
        message: msg.text || '',
        text: msg.text || undefined,
        rawText: msg.rawText || undefined,
        sender: msg.sender,
        timestamp: msg.timestamp,
        isCompressedContext: msg.isCompressedContext || undefined,
        hiddenFromChat: msg.hiddenFromChat || msg.isCompressedContext || undefined,
        agent_info: msg.agent_info || undefined,
        phases: msg.phases || undefined,
        closingText: msg.closingText || undefined,
        agentRunId: msg.agentRunId || undefined,
        chatLearningsCount: msg.chatLearningsCount || undefined,
        agentToolCalls: msg.agentToolCalls?.length > 0 ? msg.agentToolCalls : undefined,
        segments: msg.segments?.length > 0 ? msg.segments : undefined,
      })),
    };

    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
    if (!token) return;

    const baseUrl = process.env.NODE_ENV === 'production'
      ? (process.env.REACT_APP_API_URL || 'https://www.pabs.co.nz')
      : '';

    fetch(`${baseUrl}/api/save-conversation`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {});
  }

  // Save conversation to backend. Saves run one at a time: two concurrent saves
  // of a conversation that has no database id yet would each insert a copy.
  saveConversation(conversation) {
    const run = () => this.saveConversationNow(conversation);
    this._saveChain = (this._saveChain || Promise.resolve()).then(run, run);
    return this._saveChain;
  }

  async saveConversationNow(conversation) {
    if (!conversation) {
      console.log("ChatStateManager: Skipping save - no conversation object");
      return;
    }

    // Validate conversation ID
    if (!conversation.id) {
      console.error("ChatStateManager: Conversation missing ID, cannot save");
      return;
    }

    // Don't save conversations without user interaction
    if (!conversation.hasUserInteraction) {
      console.log("ChatStateManager: Skipping save - no user interaction yet");
      return;
    }

    // Determine plan ID for saving - null is valid for global conversations
    const planIdToUse = conversation.planId || this.currentPlanId || null;
    
    console.log("ChatStateManager: Saving conversation with plan ID:", planIdToUse, 
      planIdToUse ? "(plan-specific)" : "(global conversation)");

    // Filter out progress UI messages and loading messages for saving
    const saveableMessages = conversation.messages.filter(hasPersistableMessageContent);

    if (saveableMessages.length === 0) {
      console.log(
        "ChatStateManager: No saveable messages (only progress UI), skipping save",
      );
      return;
    }

    // Ensure we have meaningful content to save
    const hasRealContent = saveableMessages.some(
      (msg) =>
        msg.sender === "user" ||
        (msg.sender === "bot" &&
          msg.text &&
          !msg.text.includes("Workflow Progress")),
    );

    if (!hasRealContent) {
      console.log(
        "ChatStateManager: No meaningful content to save, skipping save",
      );
      return;
    }

    try {
      // Generate title from first USER message, not bot message
      const firstUserMessage = saveableMessages.find(
        (msg) => msg.sender === "user",
      );
      const titleSource =
        firstUserMessage?.text ||
        saveableMessages[0]?.text ||
        "New Conversation";

      const response = await fetchWithAuth("/api/save-conversation", {
        method: "POST",
        body: {
          conversation_id: String(conversation.id).startsWith("conv_")
            ? null
            : conversation.id,
          plan_id: planIdToUse,
          messages: saveableMessages.map((msg) => ({
            id: msg.id || undefined,
            message: msg.text || "",
            text: msg.text || undefined,
            rawText: msg.rawText || undefined,
            sender: msg.sender,
            timestamp: msg.timestamp,
            isCompressedContext: msg.isCompressedContext || undefined,
            hiddenFromChat: msg.hiddenFromChat || msg.isCompressedContext || undefined,
            agentExchangeLog: msg.agentExchangeLog || undefined,
            agent_info: msg.agent_info || undefined,
            phases: msg.phases ? msg.phases.map(p => ({
              id: p.id,
              state: p.state,
              label: p.label || undefined,
                tasks: Array.isArray(p.tasks) ? p.tasks.map(t => ({
                id: t.id,
                label: t.label,
                state: t.state,
                subtitle: t.subtitle || undefined,
                type: t.type || undefined,
                toolType: t.toolType || undefined,
                capability: t.capability || undefined,
                description: t.description || undefined,
                agentInfo: t.agentInfo ? {
                  id: t.agentInfo.id,
                  name: t.agentInfo.name,
                  state: t.agentInfo.state,
                  isLead: t.agentInfo.isLead,
                  instruction: t.agentInfo.instruction || undefined,
                  detail: t.agentInfo.detail?.length > 0 ? t.agentInfo.detail : undefined,
                } : undefined,
              })) : [],
            })) : undefined,
            closingText: msg.closingText || undefined,
            agentRunId: msg.agentRunId || msg.agent_run_id || undefined,
            agentRunStatus: msg.agentRunStatus || undefined,
            agentRunPhase: msg.agentRunPhase || undefined,
            source: msg.source || undefined,
            mission_id: msg.mission_id || undefined,
            agent_run_id: msg.agent_run_id || msg.agentRunId || undefined,
            task_id: msg.task_id || undefined,
            mission_state: msg.mission_state || undefined,
            agentToolCalls: msg.agentToolCalls?.length > 0 ? msg.agentToolCalls.map(tc => ({
              tool_call_id: tc.tool_call_id,
              tool:         tc.tool,
              userDescription: tc.userDescription || undefined,
              params_schema: tc.params_schema || undefined,
              params:       tc.params || undefined,
              result:       tc.result || undefined,
              status:       tc.status,
            })) : undefined,
            segments: msg.segments?.length > 0 ? msg.segments.map(seg => (
              seg.type === 'tool'
                ? {
                    type: seg.type,
                    tool_call_id: seg.tool_call_id,
                    tool: seg.tool,
                    userDescription: seg.userDescription || undefined,
                    params_schema: seg.params_schema || undefined,
                    params: seg.params || undefined,
                    result: seg.result || undefined,
                    status: seg.status,
                  }
                : {
                    type: seg.type,
                    rawContent: seg.rawContent || undefined,
                    displayContent: seg.displayContent || undefined,
                  }
            )) : undefined,
          })),
          title: this.generateConversationTitle(titleSource, conversation),
        },
      });

      const result = await response.json();
      if (response.ok && result.conversation_id) {
        const oldId = conversation.id;
        conversation.id = result.conversation_id;
        conversation.isPersisted = true;
        // The saved messages are the full conversation, so a later plan load
        // keeps this copy instead of replacing it with empty metadata.
        conversation.messagesLoaded = true;

        // Update the conversation in the map with new ID if changed
        if (String(oldId) !== String(conversation.id)) {
          this.conversations.delete(oldId);
          this.conversations.set(conversation.id, conversation);
          this.activeConversationId = conversation.id;

          console.log("ChatStateManager: Updated conversation ID", {
            oldId: oldId,
            newId: conversation.id,
            type: typeof conversation.id,
          });
        }

        console.log("ChatStateManager: Conversation saved", conversation.id);
        this.notifyListeners({
          type: "conversation_saved",
          conversationId: conversation.id,
        });
      }
    } catch (error) {
      console.error("ChatStateManager: Save failed", {
        error: error.message,
        conversationId: conversation?.id,
        planId: planIdToUse,
        messageCount: saveableMessages?.length,
        stack: error.stack,
      });

      // Try to notify user of save failure
      this.notifyListeners({
        type: "save_failed",
        conversationId: conversation?.id,
        error: error.message,
      });
    }
  }

  // Load conversations for a plan
  async loadConversationsForPlan(planId) {
    console.log("🔥🔥🔥 CHAT LOG: loadConversationsForPlan() starting for plan", planId);
    try {
      const response = await fetchWithAuth("/get-conversations", {
        method: "GET",
      });

      // Backend returns metadata only. Full messages are loaded lazily through
      // /get-conversation/:id when a conversation becomes active.
      const allConversations = await response.json();
      console.log("🔥🔥🔥 CHAT LOG: Backend response for get-conversations:", {
        ok: response.ok,
        isArray: Array.isArray(allConversations),
        totalConversations: Array.isArray(allConversations) ? allConversations.length : 0,
        conversationIds: Array.isArray(allConversations) ? allConversations.map(c => c.id) : []
      });

      if (response.ok && Array.isArray(allConversations)) {
        // Load conversations for this plan (DB field is `id`, not `conversation_id`)
        const planConversations = allConversations.filter(
          (conv) =>
            conv.plan_id === planId || conv.plan_id === parseInt(planId),
        );

        // A client saves the foundation outside the browser's creation workflow.
        // Initialize its first chat on entry, without interrupting an in-app
        // workflow or seeding a new welcome into existing chat history.
        const hasLocalMessages = Array.from(this.conversations.values()).some(
          conversation => String(conversation.planId) === String(planId) && conversation.messages?.length > 0,
        );
        if (planId && planConversations.length === 0 && !hasLocalMessages) {
          try {
            const welcomeResponse = await fetchWithAuth(`/plans/${planId}/chat-welcome`, { method: 'POST' });
            if (welcomeResponse?.ok) {
              const { conversation } = await welcomeResponse.json();
              if (conversation && String(conversation.plan_id) === String(planId)) planConversations.push(conversation);
            }
          } catch (error) {
            console.error('ChatStateManager: Could not initialize the plan welcome', error);
          }
        }

        console.log("🔥🔥🔥 CHAT LOG: Filtered conversations for plan", planId, ":", {
          filteredCount: planConversations.length,
          conversationDetails: planConversations.map(c => ({
            id: c.id,
            planId: c.plan_id,
            title: c.title,
            timestamp: c.timestamp
          }))
        });

        planConversations.forEach((conv) => {
          const existingInMemory = this.conversations.get(conv.id);
          if (
            existingInMemory?.messagesLoaded &&
            existingInMemory.messages?.length > 0
          ) {
            return;
          }

          const conversation = {
            id: conv.id,          // DB field is `id`
            planId: conv.plan_id,
            messages: [],
            title: conv.title,
            createdAt: conv.created_at,
            lastActive: conv.updated_at || conv.timestamp,
            isPersisted: true,
            messagesLoaded: false,
            hasUserInteraction: true,
          };

          this.conversations.set(conv.id, conversation);
          console.log("🔥🔥🔥 CHAT LOG: Cached conversation metadata", conv.id, "for plan", conv.plan_id);
        });

        console.log("🔥🔥🔥 CHAT LOG: Loaded", planConversations.length, "conversations for plan", planId);
      }
    } catch (error) {
      console.error("🔥🔥🔥 CHAT LOG: Failed to load conversations", error);
    }
  }

  // Update conversation with plan ID after plan is saved
  async updateConversationPlanId(planId) {
    const conversation = await this.getActiveConversation();
    if (!conversation) {
      console.error(
        "ChatStateManager: No active conversation to update with plan ID",
      );
      return;
    }

    // Only update if conversation has a database ID (not a temp conv_ ID)
    const conversationId = conversation.id;
    const isPersistedConversation = conversationId && !String(conversationId).startsWith("conv_");

    console.log("ChatStateManager: Updating conversation with plan ID", {
      conversationId,
      planId,
      isPersistedConversation,
      messageCount: conversation.messages?.length || 0,
    });

    // Update the conversation's plan ID in memory
    conversation.planId = planId;
    this.currentPlanId = planId;

    // A pending debounced save holds messages the database does not have yet.
    // Replace it with one full save that also carries the plan link, instead
    // of dropping those messages. One save also avoids a duplicate insert.
    const hadPendingSave = Boolean(this.saveTimeout);
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
      this.saveTimeout = null;
      this._pendingConversation = null;
    }

    // If conversation is already persisted in database, use direct update endpoint
    if (isPersistedConversation && !hadPendingSave) {
      console.log("🔗 ChatStateManager: Calling PATCH endpoint to link conversation", conversationId, "to plan", planId);
      try {
        const response = await fetchWithAuth(`/api/conversation/${conversationId}/plan`, {
          method: "PATCH",
          body: { plan_id: planId },
        });

        const responseData = await response.json().catch(() => ({}));
        
        if (response.ok) {
          console.log("✅ ChatStateManager: Successfully linked conversation to plan via direct update", {
            conversationId,
            planId,
            response: responseData,
          });
        } else {
          console.error("❌ ChatStateManager: Failed to update conversation plan ID", {
            status: response.status,
            conversationId,
            planId,
            error: responseData,
          });
          // Fallback to full save
          this.saveConversation(conversation);
        }
      } catch (error) {
        console.error("❌ ChatStateManager: Error updating conversation plan ID:", error);
        // Fallback to full save
        this.saveConversation(conversation);
      }
    } else {
      // Not saved yet, or unsaved messages pending: full save with the plan ID
      console.log("📝 ChatStateManager: Saving conversation with plan ID");
      await this.saveConversation(conversation);
    }

    // Notify listeners
    this.notifyListeners({
      type: "conversation_plan_updated",
      conversationId: conversation.id,
      planId: planId,
    });
  }

  // Generate conversation title
  generateConversationTitle(firstMessage, conversation = null) {
    // For new plans, use design challenge if available
    if (conversation?.designChallenge) {
      const words = conversation.designChallenge.trim().split(/\s+/);
      let title = words.slice(0, 7).join(" ");
      if (words.length > 7) {
        title += "...";
      }
      return title;
    }

    if (!firstMessage) return "New Conversation";

    const words = firstMessage.trim().split(/\s+/);
    let title = words.slice(0, 7).join(" ");
    if (words.length > 7) {
      title += "...";
    }
    return title;
  }

  // Reset (for testing/debugging only)
  reset() {
    this.conversations.clear();
    this.activeConversationId = null;
    this.currentPlanId = null;
    this.isInitialized = false;
    this._isAddingMessage = false;

    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
    }

    console.log("ChatStateManager: Reset completed");
  }
}

// Export singleton instance
const chatStateManager = ChatStateManager.getInstance();
export default chatStateManager;
